import {
  Scene,
  Group,
  Mesh,
  MeshPhongMaterial,
  MeshBasicMaterial,
  BufferGeometry,
  BufferAttribute,
  Points,
  PointsMaterial,
  Box3,
  Vector3,
  TorusGeometry,
  CircleGeometry,
  DoubleSide,
} from "three";

export class Portal {
  private portalGroup: Group;
  private portalBox: Box3;
  private isStartPortal: boolean;
  private scene: Scene;
  private particleSystem: Points;

  constructor(scene: Scene, position: Vector3, isStartPortal: boolean = false) {
    this.scene = scene;
    this.isStartPortal = isStartPortal;
    this.portalGroup = new Group();
    this.portalGroup.position.copy(position);
    this.portalGroup.rotation.x = 0.35;
    this.portalGroup.rotation.y = 0;

    // Create portal ring
    const portalGeometry = new TorusGeometry(2, 0.3, 16, 32);
    const portalMaterial = new MeshPhongMaterial({
      color: isStartPortal ? 0xff0000 : 0x00ff00,
      emissive: isStartPortal ? 0xff0000 : 0x00ff00,
      transparent: true,
      opacity: 0.8,
      shininess: 100,
    });
    const portal = new Mesh(portalGeometry, portalMaterial);
    this.portalGroup.add(portal);

    // Create portal inner surface
    const portalInnerGeometry = new CircleGeometry(1.7, 32);
    const portalInnerMaterial = new MeshBasicMaterial({
      color: isStartPortal ? 0xff0000 : 0x00ff00,
      transparent: true,
      opacity: 0.5,
      side: DoubleSide,
    });
    const portalInner = new Mesh(portalInnerGeometry, portalInnerMaterial);
    this.portalGroup.add(portalInner);

    // Create particle system
    const particleCount = 200;
    const particles = new BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount * 3; i += 3) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 2 + (Math.random() - 0.5) * 0.5;
      positions[i] = Math.cos(angle) * radius;
      positions[i + 1] = Math.sin(angle) * radius;
      positions[i + 2] = (Math.random() - 0.5) * 0.5;

      colors[i] = isStartPortal ? 0.8 + Math.random() * 0.2 : 0;
      colors[i + 1] = isStartPortal ? 0 : 0.8 + Math.random() * 0.2;
      colors[i + 2] = 0;
    }

    particles.setAttribute("position", new BufferAttribute(positions, 3));
    particles.setAttribute("color", new BufferAttribute(colors, 3));

    const particleMaterial = new PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
    });

    this.particleSystem = new Points(particles, particleMaterial);
    this.portalGroup.add(this.particleSystem);

    // Add portal to scene
    this.scene.add(this.portalGroup);

    // Create portal collision box
    this.portalBox = new Box3().setFromObject(this.portalGroup);

    // Start animation
    this.animate();
  }

  private animate = () => {
    const positions = this.particleSystem.geometry.attributes.position
      .array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      positions[i + 1] += 0.02 * Math.sin(Date.now() * 0.001 + i);
    }
    this.particleSystem.geometry.attributes.position.needsUpdate = true;
    requestAnimationFrame(this.animate);
  };

  public checkCollision(playerBox: Box3): boolean {
    const portalCenter = this.portalBox.getCenter(new Vector3());
    const playerCenter = playerBox.getCenter(new Vector3());
    const distance = playerCenter.distanceTo(portalCenter);
    return distance < 3;
  }

  public getPortalBox(): Box3 {
    return this.portalBox;
  }

  public isStart(): boolean {
    return this.isStartPortal;
  }

  public getPortalGroup(): Group {
    return this.portalGroup;
  }

  public getPosition(): Vector3 {
    return this.portalGroup.position;
  }
}
